import { clearViewState } from '../map/viewState'
import { createThemeToggle } from './themeToggle'
import { isDebugEnabled, mountDebugButton } from './debugLog'

/**
 * The topbar's single "⚙ Settings" dropdown — theme, debug (when enabled),
 * and a reset action. Follows the ☀/☾ theme toggle's convention of a plain
 * Unicode glyph instead of a hand-drawn icon (viewer/config.ts has none).
 *
 * Self-contained like createThemeToggle()/mountDebugButton(): returns a
 * mounted element, wires its own open/close, nothing else needs to know.
 */
export function createSettingsMenu(): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'settings-menu'

  const trigger = document.createElement('button')
  trigger.type = 'button'
  trigger.className = 'btn btn--small settings-menu__trigger'
  trigger.textContent = '⚙ Settings'
  trigger.setAttribute('aria-haspopup', 'true')
  trigger.setAttribute('aria-expanded', 'false')
  wrap.appendChild(trigger)

  const panel = document.createElement('div')
  panel.className = 'settings-menu__panel'
  panel.hidden = true
  wrap.appendChild(panel)

  const themeItem = document.createElement('div')
  themeItem.className = 'settings-menu__item'
  themeItem.appendChild(createThemeToggle())
  panel.appendChild(themeItem)

  if (isDebugEnabled()) {
    const debugItem = document.createElement('div')
    debugItem.className = 'settings-menu__item'
    panel.appendChild(debugItem)
    mountDebugButton(debugItem)
  }

  const resetItem = document.createElement('div')
  resetItem.className = 'settings-menu__item'
  const resetBtn = document.createElement('button')
  resetBtn.type = 'button'
  resetBtn.className = 'btn btn--small'
  resetBtn.textContent = 'Reset view'
  resetBtn.title = 'Clear the saved view and show every layer at the default extent'
  resetBtn.addEventListener('click', () => {
    clearViewState()
    location.href = location.pathname
  })
  resetItem.appendChild(resetBtn)
  panel.appendChild(resetItem)

  function close(): void {
    panel.hidden = true
    trigger.setAttribute('aria-expanded', 'false')
  }

  function open(): void {
    panel.hidden = false
    trigger.setAttribute('aria-expanded', 'true')
  }

  trigger.addEventListener('click', (event) => {
    event.stopPropagation()
    if (panel.hidden) open()
    else close()
  })

  // Outside click / Escape close the panel. This listener outlives a single
  // startMapScreen() render (the topbar is rebuilt wholesale on re-render,
  // e.g. after seat-limit recovery) but stays cheap once detached: `contains`
  // on a node no longer in the document simply returns false forever.
  document.addEventListener('click', (event) => {
    if (!panel.hidden && !wrap.contains(event.target as Node)) close()
  })
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) close()
  })

  return wrap
}
